import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Filter, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown, Check, X, Download } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { INFORMES_COLUMNS_BY_DESTINATION } from '../constants';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Informes = () => {
  const { user } = useAuth();
  const [samples, setSamples] = useState([]);
  const [filteredSamples, setFilteredSamples] = useState([]);
  const [estados, setEstados] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Sorting state
  const [sortColumn, setSortColumn] = useState('client');
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // Check if user should have restricted access
  const isRestrictedUser = user && !user.is_superuser && user.assigned_destinations?.length === 1;
  const restrictedDestination = isRestrictedUser ? user.assigned_destinations[0] : null;

  const [selectedDestination, setSelectedDestination] = useState('all');
  const [selectedEstado, setSelectedEstado] = useState('all');

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { sampleId, key }
  const [editValue, setEditValue] = useState('');

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkValue, setBulkValue] = useState('');
  const [bulkStatusIds, setBulkStatusIds] = useState(null); // null = no change, [] = clear, [...] = set
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isSavingBulk, setIsSavingBulk] = useState(false);

  const destinationEstados = estados.filter(e => selectedDestination === 'all' || e.destinations?.includes(selectedDestination));

  // Compute current states from selected samples (intersection)
  const selectedSamplesStatusIds = useMemo(() => {
    if (selectedIds.size === 0) return [];
    const selectedSamples = samples.filter(s => selectedIds.has(s.id));
    if (selectedSamples.length === 0) return [];
    // Intersection: states that ALL selected samples have
    const first = new Set(selectedSamples[0].status_id || []);
    for (let i = 1; i < selectedSamples.length; i++) {
      const current = new Set(selectedSamples[i].status_id || []);
      for (const id of first) {
        if (!current.has(id)) first.delete(id);
      }
    }
    return Array.from(first);
  }, [selectedIds, samples]);

  // The effective status ids shown in the dropdown
  const effectiveBulkStatusIds = bulkStatusIds !== null ? bulkStatusIds : selectedSamplesStatusIds;

  const toggleBulkStatus = (estadoId) => {
    const current = [...effectiveBulkStatusIds];
    const newIds = current.includes(estadoId)
      ? current.filter(id => id !== estadoId)
      : [...current, estadoId];
    setBulkStatusIds(newIds);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedSamples.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedSamples.map(s => s.id)));
    }
  };

  const handleBulkSave = async () => {
    if (selectedIds.size === 0) return;
    const hasResultado = bulkValue.trim().length > 0;
    const hasStatus = bulkStatusIds !== null;
    if (!hasResultado && !hasStatus) return;

    setIsSavingBulk(true);
    try {
      const updateData = {};
      if (hasResultado) updateData.resultado_otros_ensayos = bulkValue;
      if (hasStatus) updateData.status_id = bulkStatusIds;

      const promises = Array.from(selectedIds).map(id =>
        axios.put(`/work-samples/${id}`, updateData)
      );
      await Promise.all(promises);
      setSamples(prev => prev.map(s => {
        if (!selectedIds.has(s.id)) return s;
        const updated = { ...s };
        if (hasResultado) updated.resultado_otros_ensayos = bulkValue;
        if (hasStatus) updated.status_id = bulkStatusIds;
        return updated;
      }));
      toast.success(`${selectedIds.size} muestras actualizadas`);
      setSelectedIds(new Set());
      setBulkValue('');
      setBulkStatusIds(null);
      setShowStatusDropdown(false);
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
    setIsSavingBulk(false);
  };

  const handleStartEdit = (sampleId, key, currentValue) => {
    setEditingCell({ sampleId, key });
    setEditValue(currentValue || '');
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveEdit = useCallback(async () => {
    if (!editingCell) return;
    const { sampleId, key } = editingCell;
    try {
      await axios.put(`/work-samples/${sampleId}`, { [key]: editValue || null });
      setSamples(prev => prev.map(s => s.id === sampleId ? { ...s, [key]: editValue || null } : s));
      toast.success('Guardado correctamente');
    } catch (error) {
      toast.error('Error al guardar');
      console.error(error);
    }
    setEditingCell(null);
    setEditValue('');
  }, [editingCell, editValue]);

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveEdit();
    if (e.key === 'Escape') handleCancelEdit();
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // If already in DD/MM/YYYY format
    if (dateStr.includes('/')) {
      return dateStr.split(' ')[0];
    }
    // If in YYYY-MM-DD format
    if (dateStr.includes('-')) {
      const parts = dateStr.split(' ')[0].split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    return dateStr;
  };

  // Helper to check if color is light
  const isLightColor = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 155;
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Set default destination for restricted users
  useEffect(() => {
    if (isRestrictedUser && restrictedDestination) {
      setSelectedDestination(restrictedDestination);
    }
  }, [isRestrictedUser, restrictedDestination]);

  // Filter samples when destination or estado changes
  useEffect(() => {
    if (selectedDestination === 'all') {
      setFilteredSamples([]);
    } else {
      let filtered = samples.filter(s => s.destination === selectedDestination);
      if (selectedEstado !== 'all') {
        filtered = filtered.filter(s => {
          const ids = Array.isArray(s.status_id) ? s.status_id : (s.status_id ? [s.status_id] : []);
          return ids.includes(selectedEstado);
        });
      }
      setFilteredSamples(filtered);
    }
    setCurrentPage(1);
    setSortColumn('client');
    setSortDirection('asc');
  }, [selectedDestination, selectedEstado, samples]);

  // Reset estado filter when destination changes
  useEffect(() => {
    setSelectedEstado('all');
    setSelectedIds(new Set());
    setBulkStatusIds(null);
    setShowStatusDropdown(false);
  }, [selectedDestination]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage]);

  // Sort function
  const sortSamples = (data, column, direction) => {
    return [...data].sort((a, b) => {
      let valueA = a[column] || '';
      let valueB = b[column] || '';

      // Handle date columns
      if (column.startsWith('fecha_')) {
        // Convert to comparable date format
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(year, month - 1, day);
          }
          if (dateStr.includes('-')) {
            return new Date(dateStr);
          }
          return new Date(0);
        };
        valueA = parseDate(valueA);
        valueB = parseDate(valueB);
        
        return direction === 'asc' 
          ? valueA - valueB 
          : valueB - valueA;
      }

      // Handle string columns
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase();
        valueB = (valueB || '').toLowerCase();
        
        return direction === 'asc'
          ? valueA.localeCompare(valueB, 'es')
          : valueB.localeCompare(valueA, 'es');
      }

      // Handle numeric columns
      return direction === 'asc' ? valueA - valueB : valueB - valueA;
    });
  };

  // Sorted and paginated samples
  const sortedSamples = useMemo(() => {
    return sortSamples(filteredSamples, sortColumn, sortDirection);
  }, [filteredSamples, sortColumn, sortDirection]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedSamples.length / itemsPerPage);
  const paginatedSamples = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedSamples.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedSamples, currentPage]);

  // Handle column header click for sorting
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Get sort icon for column header
  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-40" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  const fetchData = async () => {
    try {
      const [samplesRes, estadosRes, destRes] = await Promise.all([
        axios.get('/work-samples'),
        axios.get('/estados'),
        axios.get('/destinations'),
      ]);

      setSamples(samplesRes.data);
      setEstados(estadosRes.data);
      setDestinations(destRes.data);
    } catch (error) {
      toast.error('Error al cargar los datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Get estado by ID
  const getEstadoById = (id) => {
    return estados.find((e) => e.id === id);
  };

  // Get status badges for a sample
  const getStatusBadges = (sample) => {
    // Handle both old (string) and new (array) format
    let statusIds = [];
    if (Array.isArray(sample.status_ids)) {
      statusIds = sample.status_ids;
    } else if (sample.status_id) {
      statusIds = Array.isArray(sample.status_id) ? sample.status_id : [sample.status_id];
    }

    return statusIds.map(id => getEstadoById(id)).filter(Boolean);
  };

  const currentColumns = INFORMES_COLUMNS_BY_DESTINATION[selectedDestination] || [];
  const hasEditableColumn = currentColumns.some(c => c.isEditable);

  const generatePDF = () => {
    if (sortedSamples.length === 0) {
      toast.error('No hay datos para generar el PDF');
      return;
    }

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(`Informe - ${selectedDestination}`, pageWidth / 2, 15, { align: 'center' });

    // Subtitle with filters
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const filterParts = [`Destino: ${selectedDestination}`];
    if (selectedEstado !== 'all') {
      const estado = estados.find(e => e.id === selectedEstado);
      if (estado) filterParts.push(`Estado: ${estado.name}`);
    }
    doc.text(filterParts.join(' | '), pageWidth / 2, 21, { align: 'center' });

    // Build table from current columns
    const columns = currentColumns.map(c => c.label);
    // Add "Estado" column
    columns.push('Estado');

    const rows = sortedSamples.map(sample => {
      const row = currentColumns.map(col => {
        if (col.isDate) return formatDate(sample[col.key]);
        return sample[col.key] || '';
      });
      // Add status
      const statuses = getStatusBadges(sample).map(e => e.name).join(', ');
      row.push(statuses || '');
      return row;
    });

    autoTable(doc, {
      startY: 27,
      head: [columns],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    // Footer
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    const finalY = doc.lastAutoTable.finalY + 8;
    doc.text(`Total: ${sortedSamples.length} registros`, pageWidth - 14, finalY, { align: 'right' });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`, 14, doc.internal.pageSize.getHeight() - 8);

    doc.save(`informe_${selectedDestination.toLowerCase()}.pdf`);
    toast.success('PDF generado correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="informes-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="informes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Informes</h1>
        </div>
      </div>

      {/* Filters Card */}
      <Card data-testid="informes-filters">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Destino</Label>
              <Select
                value={selectedDestination}
                onValueChange={setSelectedDestination}
                disabled={isRestrictedUser}
                data-testid="informes-destination-filter"
              >
                <SelectTrigger className={isRestrictedUser ? 'bg-gray-100' : ''}>
                  <SelectValue placeholder="Selecciona destino" />
                </SelectTrigger>
                <SelectContent>
                  {!isRestrictedUser && (
                    <SelectItem value="all">Todos los destinos</SelectItem>
                  )}
                  {destinations.map((dest) => (
                    <SelectItem key={dest.id} value={dest.name}>
                      {dest.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estado</Label>
              <Select
                value={selectedEstado}
                onValueChange={setSelectedEstado}
                data-testid="informes-estado-filter"
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {estados
                    .filter(estado => selectedDestination === 'all' || estado.destinations?.includes(selectedDestination))
                    .map((estado) => (
                    <SelectItem key={estado.id} value={estado.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: estado.color }}
                        />
                        {estado.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedDestination !== 'all' && (
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, sortedSamples.length)} de {sortedSamples.length} registros para {selectedDestination}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {selectedDestination === 'all' ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Selecciona un destino para ver los informes</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="informes-results">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Resultados - {selectedDestination}</CardTitle>
              <Button
                variant="outline"
                onClick={generatePDF}
                disabled={sortedSamples.length === 0}
                data-testid="informes-generate-pdf-btn"
              >
                <Download size={16} className="mr-2" />
                Generar PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSamples.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay muestras para este destino
              </div>
            ) : (
              <>
                {/* Bulk edit bar */}
                {hasEditableColumn && selectedIds.size > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-wrap items-center gap-3" data-testid="bulk-edit-bar">
                    <span className="text-sm font-medium text-blue-700">{selectedIds.size} seleccionadas</span>
                    <Input
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      placeholder="Resultado Otros Ensayos..."
                      className="h-8 max-w-xs text-sm"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleBulkSave(); }}
                      data-testid="bulk-edit-input"
                    />

                    {/* Multi-select estados dropdown */}
                    <div className="relative">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-sm"
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        data-testid="bulk-status-trigger"
                      >
                        <span className="flex items-center gap-1">
                          Estados
                          {effectiveBulkStatusIds.length > 0 && (
                            <Badge className="ml-1 text-xs px-1.5 py-0">{effectiveBulkStatusIds.length}</Badge>
                          )}
                        </span>
                      </Button>
                      {showStatusDropdown && (
                        <div className="absolute z-50 top-full mt-1 left-0 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="bulk-status-dropdown">
                          <div className="p-2 border-b">
                            <span className="text-xs font-medium text-gray-500">Selecciona estados</span>
                          </div>
                          {destinationEstados.map((estado) => {
                            const isChecked = effectiveBulkStatusIds.includes(estado.id);
                            return (
                              <label
                                key={estado.id}
                                className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                data-testid={`bulk-status-option-${estado.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleBulkStatus(estado.id)}
                                  className="rounded"
                                />
                                <span
                                  className="inline-block w-3 h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: estado.color }}
                                />
                                <span className="text-sm">{estado.name}</span>
                              </label>
                            );
                          })}
                          {effectiveBulkStatusIds.length > 0 && (
                            <div className="p-2 border-t">
                              <button
                                className="text-xs text-red-500 hover:text-red-700"
                                onClick={() => setBulkStatusIds([])}
                              >
                                Limpiar estados
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <Button size="sm" onClick={handleBulkSave} disabled={isSavingBulk || (!bulkValue.trim() && bulkStatusIds === null)} data-testid="bulk-edit-save">
                      {isSavingBulk ? 'Guardando...' : 'Aplicar a todas'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { setSelectedIds(new Set()); setBulkValue(''); setBulkStatusIds(null); setShowStatusDropdown(false); }}>
                      Cancelar
                    </Button>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid="informes-table">
                    <thead>
                      <tr className="bg-gray-50">
                        {hasEditableColumn && (
                          <th className="px-2 py-3 border-b w-10">
                            <input
                              type="checkbox"
                              checked={paginatedSamples.length > 0 && selectedIds.size === paginatedSamples.length}
                              onChange={toggleSelectAll}
                              className="rounded"
                              data-testid="bulk-select-all"
                            />
                          </th>
                        )}
                        {currentColumns.map((col, idx) => (
                          <th
                            key={col.key}
                            className={`px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b cursor-pointer hover:bg-gray-100 transition-colors select-none ${
                              idx === 0 ? 'min-w-[200px]' : ''
                            } ${sortColumn === col.key ? 'bg-blue-50' : ''}`}
                            onClick={() => handleSort(col.key)}
                            data-testid={`sort-${col.key}`}
                          >
                            <div className="flex items-center">
                              {col.label}
                              {getSortIcon(col.key)}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSamples.map((sample, rowIdx) => {
                        const statusBadges = getStatusBadges(sample);
                        
                        return (
                          <tr
                            key={sample.id}
                            className={`border-b hover:bg-gray-50 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${selectedIds.has(sample.id) ? 'bg-blue-50/50' : ''}`}
                            data-testid={`informes-row-${sample.id}`}
                          >
                            {hasEditableColumn && (
                              <td className="px-2 py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(sample.id)}
                                  onChange={() => toggleSelect(sample.id)}
                                  className="rounded"
                                  data-testid={`bulk-select-${sample.id}`}
                                />
                              </td>
                            )}
                            {currentColumns.map((col, colIdx) => (
                              <td key={col.key} className="px-4 py-3 text-sm text-gray-900">
                                {colIdx === 0 ? (
                                  // First column (Cliente) - include status badges
                                  <div className="flex flex-col gap-1">
                                    <span className="font-medium">{sample[col.key] || 'N/A'}</span>
                                    {statusBadges.length > 0 && (
                                      <div className="flex flex-wrap gap-1">
                                        {statusBadges.map((estado) => (
                                          <Badge
                                            key={estado.id}
                                            style={{
                                              backgroundColor: estado.color,
                                              color: isLightColor(estado.color) ? '#000' : '#fff',
                                            }}
                                            className="text-xs px-2 py-0.5"
                                          >
                                            {estado.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : col.isEditable && editingCell?.sampleId === sample.id && editingCell?.key === col.key ? (
                                  // Editable cell in edit mode
                                  <div className="flex items-center gap-1">
                                    <Input
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={handleEditKeyDown}
                                      className="h-8 text-sm"
                                      autoFocus
                                      data-testid={`edit-cell-${sample.id}-${col.key}`}
                                    />
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={handleSaveEdit}>
                                      <Check className="h-4 w-4 text-green-600" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={handleCancelEdit}>
                                      <X className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                ) : col.isEditable ? (
                                  // Editable cell in view mode
                                  <span
                                    className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded border border-transparent hover:border-blue-300 transition-colors inline-block min-w-[60px]"
                                    onClick={() => handleStartEdit(sample.id, col.key, sample[col.key])}
                                    title="Haz clic para editar"
                                    data-testid={`editable-cell-${sample.id}-${col.key}`}
                                  >
                                    {sample[col.key] || <span className="text-gray-400 italic">N/A</span>}
                                  </span>
                                ) : col.isDate ? (
                                  formatDate(sample[col.key])
                                ) : (
                                  sample[col.key] || 'N/A'
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      Página {currentPage} de {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        data-testid="informes-prev-page"
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="informes-next-page"
                      >
                        Siguiente
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Informes;
